// Wise CSV transaction history.
//
// Wise has shipped two CSV schemas the parser needs to accept:
//
//   Legacy (pre-2024):
//     TransferWise ID, Date, Amount, Currency, Description, ...
//     `Amount` is signed (negative = sent, positive = received).
//
//   Modern (2024+):
//     ID, Date, Time, Type, Direction, Source amount (after fees),
//     Source currency, Target amount (after fees), Target currency,
//     Source fee amount, Source fee currency, Status, ...
//     `Direction` (Out/In) carries the sign; both Source amount and
//     Target amount are POSITIVE magnitudes.
//
// One forgiving column-lookup helper handles both. Multi-currency is
// preserved as a per-currency account label (`Wise (USD)`, `Wise (CAD)`)
// — no FX conversion. Account kind is "debit" (Wise balance is an asset).
//
// NOTE on Python parity: scripts/loaders.py::load_wise_csv has a sign
// bug on modern files — it flips the unsigned Source amount into income
// regardless of Direction, so an "Out" transfer ends up as a negative
// (income) tx. We intentionally DIVERGE from that behavior here and use
// Direction; a `parity-skip` comment in the test pins this decision. If
// the Python loader is ever fixed, the divergence becomes redundant.

import {
  type Transaction,
  newTransaction,
  makeId,
  parseAmount,
  parseDate,
  normalizeVendor,
  isInterPortalTransfer,
  extractLast4,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

// 32 currencies Wise supports. Used by the legacy splitter that strips a
// trailing currency suffix from amounts like "1,234.56 USD".
const WISE_CURRENCIES = [
  "USD", "CAD", "GBP", "EUR", "JPY", "AUD", "INR", "PKR",
  "NZD", "CHF", "SGD", "HKD", "AED", "MXN", "BRL", "ZAR",
  "SEK", "NOK", "DKK", "PLN", "TRY", "CNY", "KRW", "THB",
  "IDR", "PHP", "MYR", "VND", "HUF", "CZK", "RON", "ILS",
] as const;

function splitCurrency(s: string): { amount: number; currency: string } {
  const t = (s ?? "").trim();
  if (!t) return { amount: 0, currency: "" };
  for (const ccy of WISE_CURRENCIES) {
    if (t === ccy) return { amount: 0, currency: ccy };
    if (t.endsWith(" " + ccy)) {
      return { amount: parseAmount(t.slice(0, -ccy.length).trim()), currency: ccy };
    }
  }
  return { amount: parseAmount(t), currency: "" };
}

// Case-insensitive lookup across Wise's column-name drift. Returns the
// first non-empty match.
function pick(row: Record<string, string>, ...keys: string[]): string {
  const norm: Record<string, string> = {};
  for (const k of Object.keys(row)) norm[k.toLowerCase().trim()] = row[k];
  for (const k of keys) {
    const v = norm[k.toLowerCase().trim()];
    if (v !== undefined && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

export async function loadWiseCsv(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  const rows = parseCsv(text);
  // Wise sometimes prepends an "Account holder name" preamble — strip
  // by finding the row containing the Date marker.
  const headerIdx = findHeaderIndex(rows, ["Date"]);
  if (headerIdx < 0 || headerIdx >= rows.length - 1) return [];
  const dicts = toDictRows(rows[headerIdx], rows.slice(headerIdx + 1));

  const out: Transaction[] = [];
  for (const row of dicts) {
    // Status filter: completed (modern) / outgoing_payment_sent / received.
    // Legacy CSVs don't carry Status; pick() returns "" and the check skips.
    const status = pick(row, "Status").toLowerCase();
    if (status && !["completed", "outgoing_payment_sent", "received"].includes(status)) {
      continue;
    }

    const dateRaw = pick(row, "Date", "Created on", "Completed at");
    const date = dateRaw ? parseDate(dateRaw) : "";
    if (!date) continue;

    // Amount + currency. Two paths:
    //   - Legacy: Amount is signed → take as-is, flip at the end.
    //   - Modern: Source amount (after fees) is positive magnitude;
    //             Direction carries the sign.
    const legacyAmount = pick(row, "Amount");
    const direction = pick(row, "Direction").toLowerCase();
    let amountVal: number;
    let currency: string;
    if (legacyAmount) {
      const split = splitCurrency(legacyAmount);
      amountVal = split.amount;
      currency = split.currency || pick(row, "Currency") || "CAD";
    } else {
      // Modern schema.
      const srcStr = pick(row, "Source amount (after fees)", "Target amount (after fees)");
      const srcSplit = splitCurrency(srcStr);
      currency = pick(row, "Source currency", "Target currency") || srcSplit.currency || "CAD";
      const magnitude = Math.abs(srcSplit.amount);
      // Out → expense (positive in our convention). In → income (negative).
      // Other Wise directions (NEUTRAL on FX conversions) default to expense
      // so they're at least visible; user can recategorize.
      amountVal = direction === "in" ? -magnitude : magnitude;
    }

    // Description: build from a few candidate fields, dedup.
    const descCandidates = [
      "Description", "Merchant", "Payee Name", "Target name",
      "Payer Name", "Source name", "Reference",
    ];
    const descParts: string[] = [];
    for (const k of descCandidates) {
      const v = pick(row, k);
      if (v && !descParts.some((p) => p.toLowerCase() === v.toLowerCase())) {
        descParts.push(v);
        if (descParts.length >= 2) break;
      }
    }
    const description = descParts.length > 0
      ? descParts.join(" — ")
      : (pick(row, "Type", "Direction") || "Wise transaction");

    if (isInterPortalTransfer(description)) continue;

    // Legacy: Wise +ve = received → income (negative). Flip.
    // Modern: amountVal already carries our convention's sign.
    const amount = legacyAmount ? -amountVal : amountVal;

    const account = `Wise (${currency})`;
    const last4 = pick(row, "Card Last Four Digits", "Card Last 4", "Card number");

    const id = await makeId("wise", account, date, amount, description);
    out.push(
      newTransaction({
        id,
        date,
        vendor_raw: description,
        vendor_key: normalizeVendor(description) || "wise",
        amount: Math.round(amount * 100) / 100,
        portal: "wise",
        account,
        account_kind: "debit",
        account_last4: extractLast4(last4),
        source_file: filename,
        raw: row,
      }),
    );
  }
  return out;
}

export const _internal = { splitCurrency, pick, WISE_CURRENCIES };
